import sumBy from 'lodash/sumBy';
import chunk from 'lodash/chunk';

const Size = {
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  XLARGE: 'XLARGE',
};

export const PIXELS_IN_CM = 37.7952755906;
export const PDF_REPORT_PIXEL_RATIO = PIXELS_IN_CM * 0.5; // NOTE: PDF reports are zoomed

export const getPDFAppPixelSize = ({ width, height }) => ({
  pixelWidth: Math.round(width * PDF_REPORT_PIXEL_RATIO),
  pixelHeight: Math.round(height * PDF_REPORT_PIXEL_RATIO),

  // NOTE: PDF report uses zoom: 0.5 for apps
  realWidth: Math.round(2 * width * PDF_REPORT_PIXEL_RATIO),
  realHeight: Math.round(2 * height * PDF_REPORT_PIXEL_RATIO),
});

const QUATER_APPS_PER_NORMAL = 4;
const QUATER_APP_TYPE = {
  type: 'QUATER',
  columns: { min: 1, max: 2 },
  rows: { min: 1, max: 10 },
  slots: 1,
  height: 8.4, // NOTE: in CM
  width: 10.3, // NOTE: in CM
};

export const REPORT_APP_GRID_TYPES = {
  NORMAL: {
    type: 'NORMAL',
    columns: { min: 1, max: 7 },
    rows: { min: 1, max: 1000 },
    slots: 1,
    height: 17.5, // NOTE: in CM
    width: 20.8, // NOTE: in CM
  },
  WIDE: {
    type: 'WIDE',
    columns: { min: 8, max: 12 },
    rows: { min: 1, max: 25 },
    slots: 2,
    height: 17.5, // NOTE: in CM
    width: 41.8, // NOTE: in CM
  },
  HALFSCREEN: {
    type: 'HALFSCREEN',
    columns: { min: 8, max: 12 },
    rows: { min: 26, max: 40 },
    slots: 4,
    height: 26.5, // NOTE: in CM
    width: 41.8, // NOTE: in CM
  },
  FULLSCREEN: {
    type: 'FULLSCREEN',
    columns: { min: 8, max: 12 },
    rows: { min: 41, max: 1000 },
    slots: 6, // NOTE: max slots count
    height: 53.3, // NOTE: in CM
    width: 41.8, // NOTE: in CM
  },
};

// Sorts apps into their proper order via coordinates
const sortByCoordinates = apps => {
  const sortedApps = [...apps];

  sortedApps.sort((a, b) => {
    const ay = a.coordinates.y;
    const by = b.coordinates.y;

    if (ay < by) {
      return -1;
    }
    if (ay > by) {
      return 1;
    }

    if (ay === by) {
      const ax = a.coordinates.x;
      const bx = b.coordinates.x;

      if (ax < bx) {
        return -1;
      }
      if (ax > bx) {
        return 1;
      }
    }

    return 0;
  });

  return sortedApps;
};

const regularReportAppGridTypes = Object.values(REPORT_APP_GRID_TYPES);

export function getAppType(
  { coordinates, isFullScreenReport },
  currentDashboardLayout,
  reportAppGridTypes = regularReportAppGridTypes
) {
  if (currentDashboardLayout === 'singleApp' || isFullScreenReport)
    return REPORT_APP_GRID_TYPES.FULLSCREEN;

  const { w, h } = coordinates;

  return reportAppGridTypes.find(
    appType =>
      w >= appType.columns.min &&
      w <= appType.columns.max &&
      h >= appType.rows.min &&
      h <= appType.rows.max
  );
}

const splitApps = (apps, currentDashboardLayout, pdfLayoutType) => {
  const isCompactLayout = pdfLayoutType === 'compact';

  if (!isCompactLayout)
    return {
      quaterApps: [],
      otherApps: apps.map(app => ({
        ...app,
        appType: getAppType(app, currentDashboardLayout, regularReportAppGridTypes),
      })),
    };

  const splittedApps = {
    quaterApps: [],
    otherApps: [],
  };
  const compactReportAppGridTypes = [QUATER_APP_TYPE, ...regularReportAppGridTypes];
  apps.forEach(app => {
    const appType = getAppType(app, currentDashboardLayout, compactReportAppGridTypes);
    const enrichedApp = { ...app, appType };
    if (appType.type === QUATER_APP_TYPE.type) splittedApps.quaterApps.push(enrichedApp);
    else splittedApps.otherApps.push(enrichedApp);
  });

  return splittedApps;
};

export function sortPDFDashboardApps(apps) {
  const { otherApps, quaterApps } = splitApps(apps);

  return [...sortByCoordinates(otherApps), ...sortByCoordinates(quaterApps)];
}

// NOTE: This code was extracted from DashboardReport.js. It's not written by dumavit originally
export function getPages(apps, currentDashboardLayout, pdfLayoutType) {
  const { otherApps, quaterApps } = splitApps(apps, currentDashboardLayout, pdfLayoutType);
  // console.log({ otherApps, quaterApps });
  const sortedOtherApps = sortByCoordinates(otherApps);
  const sortedQuaterApps = sortByCoordinates(quaterApps);
  // console.log(sortedOtherApps, sortedQuaterApps);

  const maxAppSlots = 6;
  let usedSlotsCount = 0;

  const pages = sortedOtherApps.reduce(
    (pages, app, index, apps) => {
      let { slots, type } = app.appType;

      if (type !== REPORT_APP_GRID_TYPES.FULLSCREEN.type) {
        const nextApp = apps[index + 1];

        if (nextApp) {
          const nextAppType = nextApp.appType;
          // NOTE: Place elements in the correct order and stretch them if necessary
          if (usedSlotsCount % 2 === 0 && slots === 1 && nextAppType.slots % 2 === 0) {
            ({ slots, type } = REPORT_APP_GRID_TYPES.WIDE);
          }
        } else if (index % 2 === 0) {
          ({ slots, type } = REPORT_APP_GRID_TYPES.WIDE);
        }
      }

      usedSlotsCount += slots;

      const newPageApp = { app, slots, reportAppSize: app.appType };
      let appWasAdded = false;
      const newPages = pages.map(page => {
        if (appWasAdded) return page;

        const freeAppSlots = maxAppSlots - sumBy(page, 'slots');

        if (freeAppSlots >= slots) appWasAdded = true;

        return freeAppSlots >= slots ? [...page, newPageApp] : page;
      });

      return appWasAdded ? newPages : [...newPages, [newPageApp]];
    },
    [[]]
  );

  if (!sortedQuaterApps.length) return pages;

  const lastPage = pages[pages.length - 1];
  const lastPageEmptySlotsNum = (maxAppSlots - sumBy(lastPage, 'slots')) * QUATER_APPS_PER_NORMAL;
  const enrichedQuaterApps = sortedQuaterApps.map(app => ({
    app,
    reportAppSize: QUATER_APP_TYPE,
  }));
  const lastPageWithQarterApps = [
    ...lastPage,
    ...enrichedQuaterApps.slice(0, lastPageEmptySlotsNum),
  ];
  const paginatedQuarterApps = chunk(
    enrichedQuaterApps.slice(lastPageEmptySlotsNum),
    maxAppSlots * QUATER_APPS_PER_NORMAL
  );

  return [...pages.slice(0, pages.length - 1), lastPageWithQarterApps, ...paginatedQuarterApps];
}

export function getPDFAppSize({ w }, maximized = false) {
  if (maximized) return Size.XLARGE;
  if (w >= 5) return Size.LARGE;
  if (w >= 3) return Size.MEDIUM;
  return Size.SMALL;
}
